const DB_NAME = 'beeboard-blob-cache';
const DB_VERSION = 1;
const STORE_NAME = 'blobs';

interface CacheEntry {
  path: string;
  blob: Blob;
  etag: string | null;
  timestamp: number;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'path' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h, matches backend Cache-Control max-age

export function isCacheFresh(entry: CacheEntry): boolean {
  return Date.now() - entry.timestamp < MAX_AGE_MS;
}

export async function getCachedEntry(
  path: string,
): Promise<CacheEntry | undefined> {
  try {
    const db = await openDB();

    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(path);
      request.onsuccess = () =>
        resolve(request.result as CacheEntry | undefined);
      request.onerror = () => reject(request.error);
    });
  } catch {
    return undefined;
  }
}

export async function setCachedEntry(
  path: string,
  blob: Blob,
  etag: string | null,
): Promise<void> {
  try {
    const db = await openDB();

    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const entry: CacheEntry = { path, blob, etag, timestamp: Date.now() };
      const request = store.put(entry);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch {
    // Cache write failure is non-critical
  }
}

export async function deleteCachedEntry(path: string): Promise<void> {
  try {
    const db = await openDB();

    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.delete(path);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch {
    // Cache delete failure is non-critical
  }
}
